import { Alert, Button, Flex, Popover, Skeleton, Text } from '@backstage/ui';
import {
  ListBox,
  ListBoxItem,
  type Selection,
  Separator,
} from 'react-aria-components';
import { useEffect, useRef, useState } from 'react';
import { HISTORY_DAYS, type HistoryDays } from '@internal/scorecards-common';
import { RiCheckLine, RiEqualizerLine } from '@remixicon/react';
import { useTeamHistory } from '../../hooks/useTeamHistory';
import { HistoryKpiCards } from './HistoryKpiCards';
import { HistoryChart } from './HistoryChart';
import { HistoryChecksTable } from './HistoryChecksTable';
import { RangeToggle } from './RangeToggle';
import styles from './HistoryTabPanel.module.css';

interface HistoryTabPanelProps {
  team: string;
}

export const HistoryTabPanel = ({ team }: HistoryTabPanelProps) => {
  const [days, setDays] = useState<HistoryDays>(HISTORY_DAYS[0]);
  const [selectedTrackIds, setSelectedTrackIds] = useState<Selection>(
    new Set(),
  );
  const [committedTrackIds, setCommittedTrackIds] = useState<string[]>([]);
  const [filterOpen, setFilterOpen] = useState(false);
  const filterRef = useRef<HTMLButtonElement>(null);
  const [cachedTrackItems, setCachedTrackItems] = useState<
    { id: string; name: string }[]
  >([]);

  useEffect(() => {
    const ids =
      selectedTrackIds === 'all' ? [] : ([...selectedTrackIds] as string[]);
    const timer = setTimeout(() => setCommittedTrackIds(ids), 200);
    return () => clearTimeout(timer);
  }, [selectedTrackIds]);

  useEffect(() => {
    setSelectedTrackIds(new Set());
    setCommittedTrackIds([]);
    setCachedTrackItems([]);
  }, [days]);

  const { loading, error, data } = useTeamHistory(
    team,
    days,
    committedTrackIds.length > 0 ? committedTrackIds : undefined,
  );

  useEffect(() => {
    if (!data || committedTrackIds.length > 0) return;
    const seen = new Map<string, string>();
    for (const check of data.checks) {
      if (check.trackId !== null && check.trackName) {
        seen.set(check.trackId, check.trackName);
      }
    }
    const items = [...seen.entries()].map(([id, name]) => ({ id, name }));
    if (items.length > 0) setCachedTrackItems(items);
  }, [data, committedTrackIds.length]);

  const showFilter = !error && cachedTrackItems.length > 1;
  const selectedCount =
    selectedTrackIds === 'all'
      ? cachedTrackItems.length
      : selectedTrackIds.size;
  const filterLabel =
    selectedCount === 0
      ? 'All tracks'
      : `${selectedCount} of ${cachedTrackItems.length} tracks`;

  return (
    <Flex direction="column" gap="4" p="4">
      <Flex justify="end" align="center" gap="2">
        {showFilter && (
          <>
            <Button
              ref={filterRef}
              size="small"
              variant="secondary"
              onPress={() => setFilterOpen(o => !o)}
              aria-label="Filter by track"
              iconStart={<RiEqualizerLine size={14} />}
              style={{ maxWidth: '10rem' }}
            >
              {filterLabel}
            </Button>
            <Popover
              triggerRef={filterRef}
              isOpen={filterOpen}
              onOpenChange={setFilterOpen}
              placement="bottom end"
              hideArrow
            >
              {selectedCount > 0 && (
                <>
                  <button
                    type="button"
                    className={styles.filterClear}
                    onClick={() => {
                      setSelectedTrackIds(new Set());
                      setFilterOpen(false);
                    }}
                  >
                    Clear selection
                  </button>
                  <Separator className={styles.separator} />
                </>
              )}
              <ListBox
                aria-label="Filter by track"
                selectionMode="multiple"
                selectedKeys={selectedTrackIds}
                onSelectionChange={setSelectedTrackIds}
                items={cachedTrackItems}
              >
                {(item: { id: string; name: string }) => (
                  <ListBoxItem
                    id={item.id}
                    textValue={item.name}
                    className={styles.filterItem}
                  >
                    <span className={styles.filterCheck}>
                      <RiCheckLine size={14} />
                    </span>
                    {item.name}
                  </ListBoxItem>
                )}
              </ListBox>
            </Popover>
          </>
        )}
        <RangeToggle value={days} onChange={setDays} />
      </Flex>
      {loading && !data && (
        <Flex direction="column" gap="4" aria-busy="true">
          <Flex gap="4">
            {[0, 1, 2].map(i => (
              <Skeleton key={i} width="100%" height={96} />
            ))}
          </Flex>
          <Skeleton width="100%" height={200} />
          <Skeleton width="100%" height={320} />
        </Flex>
      )}
      {error && (
        <Alert
          status="danger"
          icon
          title={`Failed to load history: ${error.message}`}
        />
      )}
      {!error && data && (
        <Flex direction="column" gap="4">
          <Separator className={styles.separator} />
          <HistoryKpiCards kpis={data.kpis} />
          <Separator className={styles.separator} />
          <Flex direction="column" gap="2">
            <Text as="h3" variant="title-x-small">
              Failing Checks History
            </Text>
            <Text as="p" variant="body-small" color="secondary">
              Total failing check instances per day across all projects, grouped
              by level.
            </Text>
            <HistoryChart daily={data.daily} />
          </Flex>
          <Separator className={styles.separator} />
          <HistoryChecksTable checks={data.checks} team={team} days={days} />
        </Flex>
      )}
    </Flex>
  );
};
